import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { EditAmountClickedProperties } from '@iwoca/frontend-tracking-library/lib/ampli';
import { IconEuro, IconPenFilled, IconPound } from '@iwoca/icons';
import { Input, Message, Modal, currencyToNumber } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from 'react-hook-form';

import { DocRequirementsDE } from './DocRequirementsDE/DocRequirementsDE';
import { schemaDE } from './EditAmount.DE.schema';
import styles from './EditAmount.module.css';
import { schemaUK } from './EditAmount.UK.schema';
import { getUpdatedApplicationBody } from '../../helpers/getApplicationBody';
import { useContextStateKey } from '../../hooks/useContextStateKey';
import { useLocale } from '../../hooks/useLocale';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { usePutApplicationMutation } from '../../lendingApi/lendingApiMutations';
import { useGetApplicationQuery } from '../../lendingApi/lendingApiQueries';
import logger from '../../utils/logger';
import { Button } from '../Button/Button';

type TProps = {
  currentStep: EditAmountClickedProperties['Current step'];
};

type TEditAmountData = {
  amount: string;
};

export const EditAmount = ({ currentStep }: TProps) => {
  const { locale, isDE, isUK } = useLocale();
  const { t } = useLingui();

  const { application } = useGetApplicationQuery();
  const { mutatePutApplication } = usePutApplicationMutation();

  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const { formatCurrency } = useNumberFormat();
  const { isMobile } = useWindowDimensions();
  const stateKey = useContextStateKey();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TEditAmountData>({
    resolver: yupResolver(isDE ? schemaDE : schemaUK),
    defaultValues: {
      amount:
        application?.requested_products?.[0].amount?.toLocaleString(locale),
    },
  });

  const formatInputCurrency = () => {
    const amount = watch('amount')?.replace(/[^0-9]+/g, '');
    if (!amount) return '';

    return parseInt(amount).toLocaleString(locale);
  };

  const onSubmit = async (values: TEditAmountData) => {
    setError(false);
    setIsSubmitting(true);

    if (!stateKey) {
      return;
    }

    try {
      await mutatePutApplication({
        stateKey: stateKey,
        body: getUpdatedApplicationBody(application, values, locale),
      });
      setShowModal(false);
    } catch (error) {
      logger.error('Failed to edit amount', {
        error: error,
      });
      setError(true);
    } finally {
      setIsSubmitting(false);
    }

    const previousAmount =
      application?.requested_products?.[0].amount?.toString();

    const newAmount = currencyToNumber(values.amount, {
      currencySymbol: true,
      locale: locale === 'de' ? 'de-DE' : locale,
      minorUnits: false,
    }).toString();

    tracking.ampli.editAmountModalSubmitted({
      'Current step': currentStep,
      'Previous amount': previousAmount || '',
      'New amount': newAmount,
    });
  };

  const handleChangeAmountClick = () => {
    tracking.ampli.editAmountClicked({
      'Current step': currentStep,
    });
    setShowModal(true);
  };

  if (!application) {
    return null;
  }

  const applicationRequestedAmount = formatCurrency(
    application?.requested_products?.[0].amount,
  );

  return (
    <>
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
        }}
        size={isMobile ? 'medium' : 'large'}
        closeIcon
        title={t`How much would you like?`}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Input
            {...register('amount')}
            value={formatInputCurrency()}
            wrapperClassName={styles.formField}
            iconLeft={isUK ? <IconPound /> : undefined}
            iconRight={isDE ? <IconEuro /> : undefined}
            placeholder={t`Up to £1,000,000`}
            error={errors.amount?.message}
            data-testid="amount-input"
          />

          {isDE && <DocRequirementsDE />}

          <Button
            className={styles.submit}
            loading={isSubmitting}
            type="submit"
            data-testid="save-change-button"
          >
            <Trans>Save change</Trans>
          </Button>
        </form>
        {error && (
          <Message
            className={styles.error}
            type="error"
            // TODO Get a DE translation for this:
            message={t`We encountered an error while updating your requested amount`}
          />
        )}
      </Modal>
      <button
        className={styles.changeAmountButton}
        onClick={handleChangeAmountClick}
        data-testid="change-amount-button"
      >
        <Trans>
          <div className={styles.youAreApplyingFor}>You’re applying for:</div>
          <div
            className={styles.amountWithPen}
            data-testid="applying-for-amount"
          >
            {applicationRequestedAmount}
            <IconPenFilled
              fontSize={16}
              className={styles.icon}
              data-testid="penFilled-icon"
            />
          </div>
        </Trans>
      </button>
    </>
  );
};
